/**
 * CreatorLabel — shows @username (or shortened wallet) for a creator.
 * Clicking navigates to /user/<walletAddress>.
 * If the creator's profile has been deleted (null), shows "@deleted" in muted gray.
 * Designed for use inside WallpaperCard and WallpaperViewer.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useRealtimeData } from '@/hooks/use-realtime-data';
import { subscribeUserProfiles, UserProfilesResponse } from '@/lib/collections/userProfiles';
import { prefetchRoute, routeModules } from '@/App';

interface CreatorLabelProps {
  walletAddress: string;
  style?: React.CSSProperties;
  className?: string;
  /** Called synchronously before navigating — use to close overlays (e.g. full-screen player) */
  onBeforeNavigate?: () => void;
}

export const CreatorLabel: React.FC<CreatorLabelProps> = ({ walletAddress, style, className, onBeforeNavigate }) => {
  const navigate = useNavigate();

  const { data: profile, loading } = useRealtimeData<UserProfilesResponse | null>(
    subscribeUserProfiles,
    !!walletAddress,
    walletAddress,
  );

  // Profile explicitly null (not loading) → creator was deleted
  const isDeleted = !loading && profile === null;

  const displayName = isDeleted
    ? '@deleted'
    : profile?.username
      ? `@${profile.username}`
      : walletAddress
        ? `${walletAddress.slice(0, 4)}...${walletAddress.slice(-4)}`
        : '';

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (!isDeleted && walletAddress) {
      onBeforeNavigate?.();
      navigate(`/user/${walletAddress}`);
    }
  };

  const baseColor = isDeleted ? 'rgba(240,240,245,0.3)' : 'rgba(167,139,250,0.85)';
  const hoverColor = isDeleted ? 'rgba(240,240,245,0.3)' : '#c4b5fd';

  return (
    <span
      onClick={handleClick}
      className={className}
      style={{
        cursor: isDeleted ? 'default' : 'pointer',
        color: baseColor,
        fontSize: 11,
        fontFamily: "'DM Mono', monospace",
        fontWeight: 500,
        letterSpacing: '0.01em',
        textDecoration: 'none',
        transition: 'color 0.15s ease',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        maxWidth: '100%',
        display: 'inline-block',
        fontStyle: isDeleted ? 'italic' : 'normal',
        ...style,
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLElement).style.color = hoverColor;
        // Warm the UserProfilePage chunk so the click-through navigation is instant
        if (!isDeleted) prefetchRoute(routeModules.userProfile);
      }}
      onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = baseColor; }}
      onPointerDown={(e) => e.stopPropagation()}
      onTouchStart={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {displayName}
    </span>
  );
};
